import { useEffect, useMemo, useState } from "react";
import { getContactsByIds } from "../utils/firebasefunctions";
import { buildClientName } from "../utils/clientNames";

// The name-building logic lives in utils/clientNames.js (pure — no React, no
// Firestore) so the call sheet can share it. Re-exported here so existing
// importers of buildClientName from this hook keep working.
export { buildClientName } from "../utils/clientNames";

// Fetches the contacts referenced by `deals` (modern ID shape only) and returns
// a lookup map plus a ready-to-render name getter (falls back to "—").
export function useDealClientNames(deals) {
  const [contactsById, setContactsById] = useState({});

  const idKey = useMemo(() => {
    const ids = Array.from(
      new Set(
        (deals || [])
          .flatMap((d) => d?.customers || [])
          // Only real, non-empty string IDs. Deals can carry empty client
          // slots (clientinfo seeds as [""]) — an empty ID makes Firestore
          // reject the whole batch, so drop them here.
          .filter((c) => typeof c === "string" && c.trim() !== ""),
      ),
    ).sort();
    return ids.join(",");
  }, [deals]);

  useEffect(() => {
    const ids = idKey ? idKey.split(",") : [];
    if (!ids.length) return;
    let cancelled = false;

    getContactsByIds(ids)
      .then((contacts) => {
        if (cancelled) return;
        const map = {};
        for (const c of contacts) {
          if (c?.id) map[c.id] = c;
          if (c?.firebaseId) map[c.firebaseId] = c;
        }
        setContactsById((prev) => ({ ...prev, ...map }));
      })
      .catch((err) => console.error("Failed to resolve client names:", err));

    return () => {
      cancelled = true;
    };
  }, [idKey]);

  return {
    contactsById,
    getClientName: (deal) => buildClientName(deal, contactsById) || "—",
  };
}
